/**
 * Adapter from the public BE form shape (`PublicForm`) to the
 * `FormDefinition` shape consumed by `@eduviaos/ui-kit`'s
 * `FormRenderer`.
 *
 * Why an adapter at all? The public BE intentionally exposes a slim
 * shape (`PublicFormFieldDTO`) that uses different field names from
 * the authenticated FormField (e.g. `conditional_visibility` vs
 * `visible_if`) so a response leak doesn't accidentally expose IA-
 * private knobs. `FormRenderer`, on the other hand, is a workspace
 * shared component built against the authenticated shape. This
 * function is the one safe place to translate between them.
 *
 * Iron Rule #11 — keep this in sync with both sides:
 *   - eduviaos-backend/app/domains/admission/public_schemas.py
 *   - eduviaos-frontend/packages/shared/src/types/forms.ts
 */
import type {
  FormDefinition,
  FormField,
  PublicForm,
  PublicFormField,
} from "@eduviaos/shared/types";

function adaptField(f: PublicFormField): FormField {
  return {
    key: f.key,
    label: f.label,
    field_type: f.field_type,
    order: f.order,
    placeholder: f.placeholder ?? undefined,
    help_text: f.description ?? undefined,
    default_value: f.default_value,
    // The public BE serialises options under `options` for selects/
    // radios but FormRenderer reads them from `validation.options`.
    // Merge them here so consumers don't need to know the split.
    validation: {
      ...f.validation,
      options: f.options ?? f.validation.options ?? null,
      conditional_required: f.conditional_required ?? f.validation.conditional_required ?? null,
    },
    visible_if: f.conditional_visibility ?? null,
  };
}

/**
 * Build a synthetic `FormDefinition` from a `PublicForm`.
 *
 * `id`, `tenant_id`, `purpose`, audit columns and `is_active_version`
 * are filled with safe constants because:
 *   - The public surface never round-trips the form back as a
 *     `FormUpdateRequest` (parents can't edit institution forms).
 *   - `FormRenderer` doesn't read any of those fields — only `fields`,
 *     `submit_button_label`, `success_message`, `name`. Synthesising
 *     them avoids changing FormRenderer's prop contract.
 */
export function adaptPublicFormToDefinition(
  publicForm: PublicForm,
  tenantSlug: string,
): FormDefinition {
  return {
    id: `public:${tenantSlug}:${publicForm.slug}`,
    tenant_id: tenantSlug,
    name: publicForm.name,
    slug: publicForm.slug,
    purpose: "admission_stage",
    purpose_ref_id: null,
    version: publicForm.version,
    is_active_version: true,
    fields: publicForm.fields.map(adaptField),
    submit_button_label: publicForm.submit_button_label,
    success_message: publicForm.success_message,
    requires_auth: publicForm.requires_auth,
    created_at: new Date(0).toISOString(),
    created_by: "system",
    updated_at: new Date(0).toISOString(),
    updated_by: "system",
  };
}
